// Fraction Kitchen: pizzas, chocolate bars, cookies and a number line. Shade, compare,
// place and add fractions by seeing them.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import type { GameModule, GameContext } from '@shell/types';
import { makeFraction, fmt, parse, fracEq, value, reduce, type FracProblem, type Frac } from '@problems/fractions';
import { makeChoices } from '@games/common/numpad';

const PROBLEMS = 6;
const SHADE = '#e2574c', CRUST = '#f0b85a', CHOC = '#8a5a3b', CHOC_SHADE = '#f5c542', LINE = '#2a2a2e';

interface Region { kind: 'slice' | 'shape' | 'cookie'; idx: number; which?: 'a' | 'b'; x: number; y: number; w: number; h: number; cx?: number; cy?: number; r?: number; a0?: number; a1?: number }

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let level = ctx.currentLevel();
  let p: FracProblem = makeFraction(level, ctx.rng);
  let shaded = new Set<number>();          // slice / cookie indices
  let shadedB = new Set<number>();         // second shape (addLike)
  let marker = 0;                          // number line position 0..max
  let commonCut: number | null = null;     // addUnlike chosen denominator
  let wrongs = 0, hinted = false, locked = false;
  let attempts = 0, correct = 0, minLevel = level;
  let regions: Region[] = [];
  let dragging = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Dish ', h('strong', null, '1'), ` of ${PROBLEMS}`), h('span.muted.small', null, ''));
  const stage = h('div');
  const prompt = h('p.center', { style: { fontWeight: '700', minHeight: '44px', padding: '2px 4px' } });
  const controls = h('div.stack', { style: { gap: '8px' } });
  host.append(head, stage, prompt, controls);

  const S = makeSurface(stage, { aspect: 0.62, draw: true, onResize: () => draw() });

  const unPointer = attachPointer(S.canvas, {
    down(pt) { if (p.kind === 'numberLine' && !locked) { dragging = true; setMarker(pt.x); } },
    move(pt) { if (dragging) setMarker(pt.x); },
    up() { dragging = false; },
    tap(pt) {
      if (locked || p.kind === 'numberLine') return;
      const r = hitRegion(pt.x, pt.y);
      if (!r) return;
      if (r.kind === 'slice' || r.kind === 'cookie') {
        const set = r.which === 'b' ? shadedB : shaded;
        if (set.has(r.idx)) set.delete(r.idx); else set.add(r.idx);
        ctx.sound('tick');
        draw();
      } else if (r.kind === 'shape' && p.kind === 'compare') {
        answerCompare(r.which!);
      }
    },
  });

  function hitRegion(x: number, y: number): Region | null {
    for (const r of regions) {
      if (r.kind === 'slice' && r.cx !== undefined) {
        const dx = x - r.cx, dy = y - r.cy!, d = Math.hypot(dx, dy);
        if (d > r.r!) continue;
        let a = Math.atan2(dy, dx); if (a < -Math.PI / 2) a += Math.PI * 2;
        if (a >= r.a0! && a < r.a1!) return r;
      } else if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return r;
    }
    return null;
  }

  function setMarker(x: number) {
    const { x0, x1 } = lineGeom();
    const max = p.kind === 'numberLine' ? p.max : 1;
    marker = Math.max(0, Math.min(max, ((x - x0) / (x1 - x0)) * max));
    draw();
  }
  function lineGeom() { return { x0: S.w * 0.08, x1: S.w * 0.92, y: S.h * 0.55 }; }

  function setup() {
    level = ctx.currentLevel();
    minLevel = Math.min(minLevel, level);
    p = makeFraction(level, ctx.rng);
    shaded = new Set(); shadedB = new Set(); marker = 0; commonCut = null;
    wrongs = 0; hinted = false; locked = false; regions = [];
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = ({ shade: 'Shade it', name: 'Name it', compare: 'Compare', equivalent: 'Same amount', addLike: 'Add', numberLine: 'Number line', mixed: 'Mixed number', addUnlike: 'Common cut', ofSet: 'Fraction of a set', times: 'Multiply' } as Record<string, string>)[p.kind];
    prompt.textContent = p.prompt;
    if (p.kind === 'addLike') { // pre-shade the two pizzas so she counts
      for (let i = 0; i < p.a.n; i++) shaded.add(i);
      for (let i = 0; i < p.b.n; i++) shadedB.add(i);
    }
    renderControls();
    draw();
  }

  function renderControls() {
    clear(controls);
    switch (p.kind) {
      case 'shade': case 'equivalent': case 'ofSet': case 'numberLine':
        controls.append(h('button.btn.primary.block', { onclick: check }, 'Check'));
        break;
      case 'compare':
        controls.append(h('p.center.muted.small', null, 'Tap the pizza or bar that shows more.'));
        break;
      case 'name': case 'addLike': case 'mixed': case 'times': {
        const ch = makeChoices(p.choices, (v) => answerChoice(String(v)));
        controls.append(ch.el);
        break;
      }
      case 'addUnlike': {
        const cuts = h('div.row.wrap', { style: { justifyContent: 'center', gap: '6px' } });
        for (let d = 2; d <= 12; d++) cuts.append(h('button.btn.small' + (commonCut === d ? '.primary' : ''), { onclick: () => pickCut(d) }, String(d)));
        controls.append(h('p.center.muted.small', null, 'Cut both bars into the same number of pieces:'), cuts);
        if (commonCut !== null && commonCut % p.a.d === 0 && commonCut % p.b.d === 0) {
          const ch = makeChoices(p.choices, (v) => answerChoice(String(v)));
          controls.append(h('p.center.muted.small', null, `Now add the pieces: ${(commonCut / p.a.d) * p.a.n} + ${(commonCut / p.b.d) * p.b.n} out of ${commonCut}.`), ch.el);
        }
        break;
      }
    }
  }

  function pickCut(d: number) {
    if (p.kind !== 'addUnlike' || locked) return;
    commonCut = d;
    ctx.sound('pop');
    if (d % p.a.d !== 0 || d % p.b.d !== 0) ctx.say(`${d} pieces does not fit both ${p.a.d}ths and ${p.b.d}ths. Try a number both go into.`);
    else ctx.say(`${d} works for both! Each bar is now cut into ${d}.`);
    renderControls(); draw();
  }

  function report(ok: boolean) {
    const ms = performance.now() - tProblem;
    attempts++;
    if (ok) correct++;
    ctx.report({ skill: 'fractions', level, correct: ok, hintUsed: hinted, ms, problemId: `frac:${p.kind}:${fmt(p.answer)}` });
  }

  function succeed(msg: string) {
    locked = true; report(true); ctx.sound('ding'); ctx.say(msg); next();
  }
  function fail(hint: string, reveal: string) {
    wrongs++; ctx.sound('buzz');
    if (wrongs >= 2) { locked = true; report(false); ctx.say(reveal); next(1700); }
    else { hinted = true; ctx.say(hint); }
  }

  function check() {
    if (locked) return;
    if (p.kind === 'shade' || p.kind === 'equivalent') {
      const got: Frac = { n: shaded.size, d: p.d };
      if (fracEq(got, p.target)) succeed(`Yes! ${shaded.size} of ${p.d} pieces is ${fmt(got)}${p.kind === 'equivalent' ? `, the same as ${fmt(p.target)}` : ''}.`);
      else {
        const need = Math.round(value(p.target) * p.d);
        fail(`You shaded ${shaded.size}. ${fmt(p.target)} of ${p.d} pieces is ${need} piece${need === 1 ? '' : 's'}. ${shaded.size < need ? 'Shade more.' : 'Unshade some.'}`, `${fmt(p.target)} of ${p.d} pieces is ${need}. Here is what it looks like.`);
        if (locked) { shaded = new Set(Array.from({ length: need }, (_, i) => i)); draw(); }
      }
    } else if (p.kind === 'ofSet') {
      const need = p.answer.n;
      if (shaded.size === need) succeed(`${fmt(p.frac)} of ${p.total} is ${need}. Yum.`);
      else {
        fail(`${fmt(p.frac)} of ${p.total}: split ${p.total} into ${p.frac.d} equal groups of ${p.total / p.frac.d}, then take ${p.frac.n} group${p.frac.n === 1 ? '' : 's'}.`, `${fmt(p.frac)} of ${p.total} is ${need} cookies.`);
        if (locked) { shaded = new Set(Array.from({ length: need }, (_, i) => i)); draw(); }
      }
    } else if (p.kind === 'numberLine') {
      const target = value(p.target);
      if (Math.abs(marker - target) <= 1 / 24) { marker = target; draw(); succeed(`Right on ${fmt(p.target)}!`); }
      else {
        fail(`Not quite. ${fmt(p.target)} means ${p.target.n} steps of 1/${p.target.d}. Move ${marker < target ? 'right' : 'left'}.`, `${fmt(p.target)} is here.`);
        if (locked) { marker = target; draw(); }
      }
    }
  }

  function answerCompare(which: 'a' | 'b') {
    if (p.kind !== 'compare' || locked) return;
    if (which === p.bigger) succeed(`${fmt(p.answer)} is bigger. Look how much more is shaded.`);
    else fail(`Look at the shaded parts: ${fmt(p.a)} is ${value(p.a) > value(p.b) ? 'more' : 'less'} than ${fmt(p.b)}. Tap the bigger one.`, `${fmt(p.answer)} is the bigger fraction.`);
  }

  function answerChoice(s: string) {
    if (locked || !('choices' in p)) return;
    const got = parse(s);
    if (fracEq(got, p.answer)) {
      const extra = p.kind === 'addUnlike' ? ` (${fmt(p.a)} + ${fmt(p.b)} with a common cut of ${p.common})` : '';
      succeed(`Yes! ${fmt(p.answer)}${extra}.`);
    } else {
      let hint = 'Not that one. Count again.';
      if (p.kind === 'name') hint = `Count the shaded pieces, then the total. ${p.shaded} shaded out of ${p.d}.`;
      if (p.kind === 'addLike') hint = `Same size pieces, so add the tops: ${p.a.n} + ${p.b.n} = ${p.a.n + p.b.n} ${p.a.d}ths.`;
      if (p.kind === 'mixed') hint = `${p.whole} whole pizza${p.whole > 1 ? 's' : ''} and ${fmt(p.part)} more. Say it: ${p.whole} and ${fmt(p.part)}.`;
      if (p.kind === 'addUnlike') hint = `Cut both into ${p.common}. Then ${(p.common / p.a.d) * p.a.n} + ${(p.common / p.b.d) * p.b.n} pieces out of ${p.common}, then make it as simple as you can.`;
      if (p.kind === 'times') hint = `${p.whole} groups of ${fmt(p.frac)} is ${p.frac.n * p.whole} ${p.frac.d}ths. Simplify if you can.`;
      fail(hint, `The answer is ${fmt(p.answer)}.`);
    }
  }

  function next(delay = 1100) {
    n++;
    setTimeout(() => {
      if (n >= PROBLEMS) {
        if (attempts === correct && minLevel >= 5) ctx.charm('charm.pizza');
        ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      } else setup();
    }, delay);
  }

  // ---------- drawing ----------
  function drawPizza(c: CanvasRenderingContext2D, cx: number, cy: number, r: number, d: number, set: Set<number>, which: 'a' | 'b' | undefined, tappable: boolean) {
    c.fillStyle = CRUST; c.beginPath(); c.arc(cx, cy, r, 0, Math.PI * 2); c.fill();
    for (let i = 0; i < d; i++) {
      const a0 = -Math.PI / 2 + (i / d) * Math.PI * 2, a1 = -Math.PI / 2 + ((i + 1) / d) * Math.PI * 2;
      c.fillStyle = set.has(i) ? SHADE : '#fbe7c6';
      c.beginPath(); c.moveTo(cx, cy); c.arc(cx, cy, r * 0.9, a0, a1); c.closePath(); c.fill();
      c.strokeStyle = CRUST; c.lineWidth = Math.max(2, r * 0.05); c.stroke();
      if (tappable) regions.push({ kind: 'slice', idx: i, which, x: 0, y: 0, w: 0, h: 0, cx, cy, r, a0, a1 });
    }
    c.strokeStyle = LINE; c.lineWidth = 2; c.beginPath(); c.arc(cx, cy, r, 0, Math.PI * 2); c.stroke();
  }
  function drawBar(c: CanvasRenderingContext2D, x: number, y: number, w: number, hgt: number, d: number, set: Set<number>, which: 'a' | 'b' | undefined, tappable: boolean) {
    roundRect(c, x, y, w, hgt, 6); c.fillStyle = CHOC; c.fill();
    const pw = w / d;
    for (let i = 0; i < d; i++) {
      c.fillStyle = set.has(i) ? CHOC_SHADE : '#a86f49';
      c.fillRect(x + i * pw + 2, y + 3, pw - 4, hgt - 6);
      if (tappable) regions.push({ kind: 'slice', idx: i, which, x: x + i * pw, y, w: pw, h: hgt });
    }
  }
  function label(c: CanvasRenderingContext2D, text: string, x: number, y: number, size: number) {
    c.fillStyle = LINE; c.font = `800 ${Math.round(size)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText(text, x, y);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    regions = [];
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    switch (p.kind) {
      case 'shade': case 'name': {
        const d = p.d, set = p.kind === 'name' ? new Set(Array.from({ length: p.shaded }, (_, i) => i)) : shaded;
        if (p.shape === 'pizza') drawPizza(c, W / 2, H / 2, Math.min(W, H) * 0.4, d, set, undefined, p.kind === 'shade');
        else drawBar(c, W * 0.1, H * 0.35, W * 0.8, H * 0.3, d, set, undefined, p.kind === 'shade');
        if (p.kind === 'shade') label(c, `${shaded.size} of ${d} shaded`, W / 2, H * 0.92, 15);
        break;
      }
      case 'equivalent': {
        label(c, `${fmt(p.target)} looks like this:`, W / 2, H * 0.1, 14);
        drawBar(c, W * 0.1, H * 0.16, W * 0.8, H * 0.2, p.target.d, new Set(Array.from({ length: p.target.n }, (_, i) => i)), undefined, false);
        label(c, `Your bar, cut into ${p.d}:`, W / 2, H * 0.5, 14);
        drawBar(c, W * 0.1, H * 0.56, W * 0.8, H * 0.24, p.d, shaded, undefined, true);
        label(c, `${shaded.size} of ${p.d} shaded`, W / 2, H * 0.92, 14);
        break;
      }
      case 'compare': {
        const r = Math.min(W * 0.2, H * 0.3);
        drawPizza(c, W * 0.27, H * 0.45, r, p.a.d, new Set(Array.from({ length: p.a.n }, (_, i) => i)), 'a', false);
        drawPizza(c, W * 0.73, H * 0.45, r, p.b.d, new Set(Array.from({ length: p.b.n }, (_, i) => i)), 'b', false);
        regions.push({ kind: 'shape', idx: 0, which: 'a', x: 0, y: 0, w: W / 2, h: H });
        regions.push({ kind: 'shape', idx: 1, which: 'b', x: W / 2, y: 0, w: W / 2, h: H });
        label(c, fmt(p.a), W * 0.27, H * 0.88, 24); label(c, fmt(p.b), W * 0.73, H * 0.88, 24);
        break;
      }
      case 'addLike': {
        const r = Math.min(W * 0.18, H * 0.28);
        drawPizza(c, W * 0.25, H * 0.42, r, p.a.d, shaded, 'a', false);
        label(c, '+', W * 0.5, H * 0.42, 34);
        drawPizza(c, W * 0.75, H * 0.42, r, p.b.d, shadedB, 'b', false);
        label(c, fmt(p.a), W * 0.25, H * 0.86, 22); label(c, fmt(p.b), W * 0.75, H * 0.86, 22);
        break;
      }
      case 'numberLine': {
        const { x0, x1, y } = lineGeom();
        const max = p.max;
        c.strokeStyle = LINE; c.lineWidth = 3; c.beginPath(); c.moveTo(x0, y); c.lineTo(x1, y); c.stroke();
        const d = p.target.d;
        for (let i = 0; i <= d * max; i++) {
          const x = x0 + ((i / d) / max) * (x1 - x0);
          const whole = i % d === 0;
          c.beginPath(); c.moveTo(x, y - (whole ? 16 : 8)); c.lineTo(x, y + (whole ? 16 : 8)); c.stroke();
          if (whole) label(c, String(i / d), x, y + 32, 16);
        }
        label(c, `Each small tick is 1/${d}`, W / 2, H * 0.15, 14);
        const mx = x0 + (marker / max) * (x1 - x0);
        c.fillStyle = SHADE; c.beginPath(); c.moveTo(mx, y - 6); c.lineTo(mx - 14, y - 34); c.lineTo(mx + 14, y - 34); c.closePath(); c.fill();
        break;
      }
      case 'mixed': {
        const r = Math.min(W * 0.13, H * 0.26);
        const count = p.whole + 1, gap = W / (count + 1);
        for (let i = 0; i < count; i++) {
          const full = i < p.whole;
          drawPizza(c, gap * (i + 1), H * 0.45, r, p.part.d, new Set(Array.from({ length: full ? p.part.d : p.part.n }, (_, k) => k)), undefined, false);
        }
        label(c, `${p.whole} whole and a part`, W / 2, H * 0.88, 15);
        break;
      }
      case 'addUnlike': {
        const cut = commonCut !== null && commonCut % p.a.d === 0 && commonCut % p.b.d === 0 ? commonCut : null;
        const da = cut ?? p.a.d, db = cut ?? p.b.d;
        const na = cut ? (cut / p.a.d) * p.a.n : p.a.n, nb = cut ? (cut / p.b.d) * p.b.n : p.b.n;
        label(c, fmt(p.a) + (cut ? ` = ${na}/${cut}` : ''), W / 2, H * 0.1, 16);
        drawBar(c, W * 0.1, H * 0.16, W * 0.8, H * 0.2, da, new Set(Array.from({ length: na }, (_, i) => i)), undefined, false);
        label(c, fmt(p.b) + (cut ? ` = ${nb}/${cut}` : ''), W / 2, H * 0.5, 16);
        drawBar(c, W * 0.1, H * 0.56, W * 0.8, H * 0.2, db, new Set(Array.from({ length: nb }, (_, i) => i)), undefined, false);
        if (!cut) label(c, 'Different size pieces cannot be added yet.', W / 2, H * 0.9, 13);
        break;
      }
      case 'ofSet': {
        const cols = p.total <= 6 ? p.total : p.total <= 8 ? 4 : 6;
        const rows = Math.ceil(p.total / cols);
        const cell = Math.min((W * 0.9) / cols, (H * 0.8) / rows);
        const x0 = (W - cell * cols) / 2, y0 = (H - cell * rows) / 2;
        for (let i = 0; i < p.total; i++) {
          const cx = x0 + (i % cols) * cell + cell / 2, cy = y0 + Math.floor(i / cols) * cell + cell / 2, r = cell * 0.38;
          c.fillStyle = shaded.has(i) ? SHADE : '#d9a441'; c.beginPath(); c.arc(cx, cy, r, 0, 7); c.fill();
          c.fillStyle = shaded.has(i) ? '#fff' : CHOC; for (let k = 0; k < 4; k++) { c.beginPath(); c.arc(cx + Math.cos(k * 1.7) * r * 0.45, cy + Math.sin(k * 2.3) * r * 0.45, r * 0.12, 0, 7); c.fill(); }
          regions.push({ kind: 'cookie', idx: i, x: cx - cell / 2, y: cy - cell / 2, w: cell, h: cell });
        }
        label(c, `${shaded.size} of ${p.total} picked`, W / 2, H * 0.95, 13);
        break;
      }
      case 'times': {
        const r = Math.min(W / (p.whole + 1) * 0.42, H * 0.26);
        const gap = W / (p.whole + 1);
        for (let i = 0; i < p.whole; i++) drawPizza(c, gap * (i + 1), H * 0.42, r, p.frac.d, new Set(Array.from({ length: p.frac.n }, (_, k) => k)), undefined, false);
        label(c, `${p.whole} groups of ${fmt(p.frac)}`, W / 2, H * 0.86, 16);
        break;
      }
    }
    void reduce;
  }

  setup();
  return { destroy() { unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.fraction-kitchen',
  title: 'Fraction Kitchen',
  blurb: 'Pizzas, chocolate bars and cookies. Shade, compare and add fractions you can see.',
  category: 'math',
  skills: ['fractions'],
  levelRange: [1, 10],
  unlock: { stars: 9 },
  mount,
};
export default mod;
